import React, { useEffect, useState } from "react";
import { useNotification } from "../../../../NotificationContext";
import LoaderComponent from "../../../../components/LoaderComponent";
import { format } from "date-fns";
import { getIPDVisitBilling, getVisitTransactions, createIPDInvoice, getVisit, getVisitInsuranceClaim, getVisitInsuranceClaimsList, saveVisitInsuranceClaim, updateVisitInsuranceClaim, getVisitEntriesTimeline } from "../../../../apis/visit";
import { getInsuranceProviders } from "../../../../apis/insuranceProviders";
import { processPayment } from "../../../../apis/payment";
import { savePatientTransaction } from "../../../../apis/patientTransaction";
import { getPatientTransactions } from "../../../../apis/patientTransaction";
import PaymentModal, { PaymentData } from "../../../../components/PaymentModal";

interface IPDBillingModalProps {
  isOpen: boolean;
  onClose: () => void;
  visitId: number;
  patientId: number;
}

interface BillingData {
  pharmacy: Array<{
    id: number;
    saleNumber: string;
    saleDate: string;
    items: Array<{
      name: string;
      quantity: number;
      sellingPrice: number;
      discountPercentage: number;
      taxPercentage: number;
      total: number;
    }>;
    totalAmount: number;
    discountAmount: number;
    taxAmount: number;
    netAmount: number;
  }>;
  labOrders: Array<{
    id: number;
    orderNumber: string;
    orderDate: string;
    items: Array<{
      name: string;
      fee: number;
    }>;
    totalAmount: number;
  }>;
  services: Array<{
    id: number;
    name: string;
    fee: number;
    quantity: number;
    total: number;
  }>;
  roomCharges?: Array<{
    id: number;
    roomName: string;
    roomType: string;
    fromDate: string;
    toDate: string;
    durationDays: number;
    durationHours: number;
    charge: number;
  }>;
  patientBalance?: number;
}

type NavigationItem = "summary" | "billItems" | "payments" | "insurance";

interface InsuranceClaimForm {
  insuranceProviderId: number | null;
  policyNumber: string;
  coverageType: "reimbursement" | "cashless";
  claimedAmount: string;
  approvedAmount: string;
  rejectedAmount: string;
  status: string;
  claimDate: string;
  approvalDate: string;
  remarks: string;
}

const IPDBillingModal: React.FC<IPDBillingModalProps> = ({
  isOpen,
  onClose,
  visitId,
  patientId,
}) => {
  const { showNotification } = useNotification();
  const [billingData, setBillingData] = useState<BillingData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeNav, setActiveNav] = useState<NavigationItem>("summary");
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    pharmacy: false,
    labOrders: false,
    services: false,
    roomCharges: false,
  });
  const [visitTransactions, setVisitTransactions] = useState<any[]>([]);
  const [invoiceData, setInvoiceData] = useState<any>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isAdvanceModalOpen, setIsAdvanceModalOpen] = useState(false);
  const [rateOverrides, setRateOverrides] = useState<Record<string, number>>({});
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [advanceBalance, setAdvanceBalance] = useState<number | null>(null);
  const [customItems, setCustomItems] = useState<
    Array<{ id: string; name: string; quantity: number; rate: number }>
  >([]);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentContext, setPaymentContext] = useState<"selected" | "outstanding">("outstanding");
  const [visit, setVisit] = useState<any>(null);
  const [insuranceProviders, setInsuranceProviders] = useState<Array<{ id: number; name: string }>>([]);
  const [insuranceForm, setInsuranceForm] = useState<InsuranceClaimForm>({
    insuranceProviderId: null,
    policyNumber: "",
    coverageType: "reimbursement",
    claimedAmount: "",
    approvedAmount: "",
    rejectedAmount: "",
    status: "",
    claimDate: "",
    approvalDate: "",
    remarks: "",
  });
  const [isSavingInsurance, setIsSavingInsurance] = useState(false);
  const [isLoadingInsurance, setIsLoadingInsurance] = useState(false);
  const [insuranceViewMode, setInsuranceViewMode] = useState<"list" | "form">("list");
  const [insuranceClaimsList, setInsuranceClaimsList] = useState<any[]>([]);
  const [editingClaimId, setEditingClaimId] = useState<number | null>(null);
  const [defaultInsuranceFormFromAdmission, setDefaultInsuranceFormFromAdmission] = useState<InsuranceClaimForm | null>(null);

  const hasInvoice = Boolean(invoiceData?.invoice);

  const buildRateOverrides = (data: BillingData) => {
    const overrides: Record<string, number> = {};
    data.pharmacy.forEach((sale) => {
      overrides[`pharmacy-${sale.id}`] = sale.netAmount || 0;
    });
    data.labOrders.forEach((order) => {
      overrides[`lab-${order.id}`] = order.totalAmount || 0;
    });
    data.services.forEach((service) => {
      overrides[`service-${service.id}`] = service.total || 0;
    });
    data.roomCharges?.forEach((room, idx) => {
      overrides[`room-${room.id}-${idx}`] = room.charge || 0;
    });
    return overrides;
  };

  const addCustomItem = () => {
    setCustomItems((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        name: "",
        quantity: 1,
        rate: 0,
      },
    ]);
  };

  const updateCustomItem = (
    id: string,
    updates: Partial<{ name: string; quantity: number; rate: number }>
  ) => {
    setCustomItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
  };

  const removeCustomItem = (id: string) => {
    setCustomItems((prev) => prev.filter((item) => item.id !== id));
  };

  useEffect(() => {
    if (isOpen && visitId) {
      fetchBillingData();
      if (activeNav === "payments") {
        fetchPaymentData();
      }
      if (activeNav === "insurance") {
        fetchInsuranceData();
      }
    }
  }, [isOpen, visitId, activeNav]);

  useEffect(() => {
    if (billingData && !hasInvoice) {
      setRateOverrides(buildRateOverrides(billingData));
    }
  }, [billingData, hasInvoice]);

  const fetchBillingData = async () => {
    setIsLoading(true);
    try {
      const response = await getIPDVisitBilling(visitId);
      setBillingData(response as BillingData);
    } catch (error: any) {
      console.error("Failed to fetch billing data:", error);
      showNotification(
        "error",
        "Error",
        error?.response?.data?.message || "Failed to load billing data",
        "topRight"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPaymentData = async () => {
    try {
      // Fetch invoice data (includes visit-level transactions and allocations)
      const invoiceRes: any = await getVisitTransactions(visitId);
      const invoicePayload = invoiceRes?.data || null;
      setInvoiceData(invoicePayload);
      setVisitTransactions(invoicePayload?.invoice?.transactions || []);
      const balanceFromInvoice =
        invoicePayload?.patientBalance ??
        0;
      setAdvanceBalance(balanceFromInvoice);

    } catch (error: any) {
      console.error("Failed to fetch payment data:", error);
    }
  };

  const fetchInsuranceData = async () => {
    setIsLoadingInsurance(true);
    try {
      // Fetch visit (has meta from admission), timeline (admission entry form_data), and providers.
      // Do NOT require insurance-claim API on load so billing works even before backend adds that route.
      const [visitRes, timelineRes, providersRes]: any[] = await Promise.all([
        getVisit(visitId),
        getVisitEntriesTimeline(visitId).catch(() => ({ data: [] })),
        getInsuranceProviders({ per_page: 1000 }),
      ]);
      const visitData = visitRes?.data ?? visitRes;
      setVisit(visitData);
      const providersList = providersRes?.data?.data ?? providersRes?.data ?? providersRes ?? [];
      setInsuranceProviders(Array.isArray(providersList) ? providersList : []);

      // Fetch all insurance claims for this visit (list; backend may return array or single)
      let claimsList: any[] = [];
      try {
        claimsList = await getVisitInsuranceClaimsList(visitId);
      } catch {
        // Route may not exist yet
      }
      setInsuranceClaimsList(Array.isArray(claimsList) ? claimsList : []);

      const meta = visitData?.meta ?? {};
      const entries = timelineRes?.data ?? timelineRes?.entries ?? timelineRes ?? [];
      const admissionEntry = Array.isArray(entries)
        ? entries.find((e: any) => (e.entryType || e.entry_type || "").toLowerCase().includes("admission"))
        : null;
      const admissionData = admissionEntry?.formData ?? admissionEntry?.form_data ?? admissionEntry?.data ?? {};

      // Admission insurance: from visit.meta first, then from admission entry form_data
      const fromMeta = {
        insuranceProviderId: meta.insuranceProviderId ?? meta.insurance_provider_id ?? null,
        policyNumber: meta.policyNumber ?? meta.policy_number ?? "",
        coverageType: (meta.coverageType ?? meta.coverage_type ?? "reimbursement") as "reimbursement" | "cashless",
        providerName:
          meta.insuranceProvider ??
          meta.insuranceProviderName ??
          meta.insurance_provider ??
          meta.insurance_provider_name ??
          "",
      };
      const fromAdmission = {
        insuranceProviderId: admissionData.insuranceProviderId ?? admissionData.insurance_provider_id ?? null,
        policyNumber: admissionData.policyNumber ?? admissionData.policy_number ?? "",
        coverageType: (admissionData.coverageType ?? admissionData.coverage_type ?? "reimbursement") as "reimbursement" | "cashless",
        providerName:
          admissionData.insuranceProvider?.name ??
          admissionData.insuranceProviderName ??
          admissionData.insurance_provider?.name ??
          admissionData.insurance_provider_name ??
          (typeof admissionData.insuranceProvider === "string" ? admissionData.insuranceProvider : "") ??
          "",
      };

      // Build default form from admission only (for "Add insurance claim")
      let resolvedProviderId =
        fromMeta.insuranceProviderId ?? fromAdmission.insuranceProviderId ?? null;
      const providerNameSource =
        (fromMeta.providerName || fromAdmission.providerName || "").trim();
      if (!resolvedProviderId && providerNameSource && Array.isArray(providersList)) {
        const match = providersList.find((p: any) =>
          String(p.name || "").trim().toLowerCase() === providerNameSource.toLowerCase()
        );
        if (match) resolvedProviderId = match.id;
      }
      const policyNumber =
        (fromMeta.policyNumber || fromAdmission.policyNumber || "").trim();
      const coverageType =
        (fromMeta.coverageType ?? fromAdmission.coverageType) || "reimbursement";
      setDefaultInsuranceFormFromAdmission({
        insuranceProviderId: resolvedProviderId ?? null,
        policyNumber,
        coverageType: coverageType as "reimbursement" | "cashless",
        claimedAmount: "",
        approvedAmount: "",
        rejectedAmount: "",
        status: "",
        claimDate: "",
        approvalDate: "",
        remarks: "",
      });
    } catch (error: any) {
      console.error("Failed to fetch insurance data:", error);
      showNotification("error", "Error", error?.response?.data?.message || "Failed to load insurance data", "topRight");
    } finally {
      setIsLoadingInsurance(false);
    }
  };

  const formatCurrency = (amount: number | string | null | undefined) => {
    if (amount === null || amount === undefined) {
      return "₹0.00";
    }
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    if (isNaN(num)) {
      return "₹0.00";
    }
    return `₹${num.toFixed(2)}`;
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "-";
      }
      return format(date, "dd MMM yyyy hh:mm a");
    } catch (error) {
      return "-";
    }
  };

  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "-";
      }
      return format(date, "dd MMM yyyy, HH:mm");
    } catch (error) {
      return "-";
    }
  };

  const calculatePharmacyTotal = () => {
    if (!billingData) return 0;
    return billingData.pharmacy.reduce((sum, sale) => sum + (sale.netAmount || 0), 0);
  };

  const calculateLabTotal = () => {
    if (!billingData) return 0;
    return billingData.labOrders.reduce((sum, order) => {
      if (order.totalAmount && order.totalAmount > 0) {
        return sum + order.totalAmount;
      }
      const itemsTotal = order.items?.reduce((itemSum, item) => itemSum + (item.fee || 0), 0) || 0;
      return sum + itemsTotal;
    }, 0);
  };

  const calculateServicesTotal = () => {
    if (!billingData) return 0;
    return billingData.services.reduce((sum, service) => sum + (service.total || 0), 0);
  };

  const calculateRoomChargesTotal = () => {
    if (!billingData || !billingData.roomCharges) return 0;
    return billingData.roomCharges.reduce((sum, room) => sum + (room.charge || 0), 0);
  };

  const calculateGrandTotal = () => {
    return calculatePharmacyTotal() + calculateLabTotal() + calculateServicesTotal() + calculateRoomChargesTotal();
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const calculateOutstanding = () => {
    const grandTotal = calculateGrandTotal();
    const totalAllocated = invoiceData?.invoice?.paidAmount || 0;
    return Math.max(0, grandTotal - totalAllocated);
  };

  const calculateUnallocatedBalance = () => {
    const totalTransactions = visitTransactions.reduce(
      (sum, txn) => sum + (parseFloat(txn.amount) || 0),
      0
    );
    console.log({ totalTransactions });
    const totalAllocated = invoiceData?.invoice?.paymentAllocations?.reduce(
      (sum: number, alloc: any) => sum + (parseFloat(alloc.allocatedAmount) || 0),
      0
    ) || 0;
    return Math.max(0, totalTransactions - totalAllocated);
  };

  const getAdvanceBalance = () => {
    console.log({ advanceBalance });
    if (advanceBalance !== null && advanceBalance !== undefined) {
      return advanceBalance;
    }
    console.log({ billingData });
    if (billingData?.patientBalance !== undefined && billingData?.patientBalance !== null) {
      return billingData.patientBalance;
    }
    return calculateUnallocatedBalance();
  };

  const getRateValue = (key: string, fallback: number) => {
    const value = rateOverrides[key];
    if (value === undefined || value === null || Number.isNaN(value)) {
      return fallback;
    }
    return value;
  };

  const calculateEditableTotal = () => {
    if (!billingData) return 0;
    let total = 0;
    billingData.pharmacy.forEach((sale) => {
      total += getRateValue(`pharmacy-${sale.id}`, sale.netAmount || 0);
    });
    billingData.labOrders.forEach((order) => {
      total += getRateValue(`lab-${order.id}`, order.totalAmount || 0);
    });
    billingData.services.forEach((service) => {
      total += getRateValue(`service-${service.id}`, service.total || 0);
    });
    billingData.roomCharges?.forEach((room, idx) => {
      total += getRateValue(`room-${room.id}-${idx}`, room.charge || 0);
    });
    customItems.forEach((item) => {
      total += (item.quantity || 0) * (item.rate || 0);
    });
    return total;
  };

  const handleAdvanceSave = async (paymentData: PaymentData) => {
    if (!patientId) return;

    try {
      const paymentModes = [
        { mode: "cash", amount: paymentData.cash },
        { mode: "upi", amount: paymentData.upi },
        { mode: "card", amount: paymentData.card },
        { mode: "cheque", amount: paymentData.cheque },
        { mode: "online", amount: paymentData.online },
      ];

      const savePromises = paymentModes
        .filter((p) => p.amount > 0)
        .map((payment) =>
          savePatientTransaction({
            patientId,
            amount: payment.amount,
            paymentMode: payment.mode,
            note: paymentData.paymentNote || "",
            date: format(new Date(), "yyyy-MM-dd HH:mm").toString(),
          })
        );

      await Promise.all(savePromises);
      showNotification("success", "Advance collected successfully", "", "topRight");
      setIsAdvanceModalOpen(false);
      fetchPaymentData();
    } catch (err: any) {
      showNotification(
        "error",
        "Failed to collect advance",
        err?.message || "",
        "topRight"
      );
    }
  };

  const buildInvoiceItems = () => {
    if (!billingData) return [];
    const items: Array<{
      itemType: string;
      refId: number;
      quantity: number;
      description: string;
      unitPrice: number;
      amount: number;
      name?: string;
    }> = [];

    billingData.pharmacy.forEach((sale) => {
      const amount = getRateValue(`pharmacy-${sale.id}`, sale.netAmount || 0);
      items.push({
        itemType: "pharmacy",
        refId: sale.id,
        quantity: 1,
        description: sale.items.map((item) => item.name).join(", "),
        unitPrice: amount,
        amount,
      });
    });

    billingData.labOrders.forEach((order) => {
      const amount = getRateValue(`lab-${order.id}`, order.totalAmount || 0);
      items.push({
        itemType: "lab",
        refId: order.id,
        quantity: 1,
        description: `Lab Order #${order.orderNumber} - ${order.orderDate}`,
        unitPrice: amount,
        amount,
      });
    });

    billingData.services.forEach((service) => {
      const amount = getRateValue(`service-${service.id}`, service.total || 0);
      const quantity = service.quantity || 1;
      items.push({
        itemType: "service",
        refId: service.id,
        quantity,
        description: `Service #${service.id} - ${service.name}`,
        unitPrice: quantity > 0 ? amount / quantity : amount,
        amount,
      });
    });

    billingData.roomCharges?.forEach((room, idx) => {
      const amount = getRateValue(`room-${room.id}-${idx}`, room.charge || 0);
      const quantity = 1;
      items.push({
        itemType: "room",
        description: `Room ${room.roomName} (${room.roomType})`,
        refId: room.id,
        quantity,
        unitPrice: quantity > 0 ? amount / quantity : amount,
        amount,
      });
    });

    customItems
      .filter((item) => item.name.trim() && item.quantity > 0)
      .forEach((item) => {
        const quantity = item.quantity || 1;
        const rate = item.rate || 0;
        items.push({
          itemType: "custom",
          description: item.name.trim(),
          refId: 0,
          name: item.name.trim(),
          quantity,
          unitPrice: rate,
          amount: quantity * rate,
        });
      });

    return items;
  };

  const handleGenerateInvoice = async () => {
    if (!billingData || isGeneratingInvoice) return;
    const items = buildInvoiceItems();
    if (items.length === 0) {
      showNotification("warning", "No bill items available", "", "topRight");
      return;
    }
    setIsGeneratingInvoice(true);
    try {
      await createIPDInvoice(visitId, { items });
      showNotification("success", "Bill generated successfully", "", "topRight");
      fetchPaymentData();
      fetchBillingData();
    } catch (err: any) {
      showNotification(
        "error",
        "Failed to generate bill",
        err?.response?.data?.message || err?.message || "",
        "topRight"
      );
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const handleItemSelection = (itemKey: string) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemKey)) {
        newSet.delete(itemKey);
      } else {
        newSet.add(itemKey);
      }
      return newSet;
    });
  };

  const getSelectedItemsTotal = () => {
    if (!billingData) return 0;
    let total = 0;

    selectedItems.forEach((key) => {
      const [type, id] = key.split("-");
      if (type === "pharmacy") {
        const sale = billingData.pharmacy.find((s) => s.id === parseInt(id));
        if (sale) total += sale.netAmount || 0;
      } else if (type === "lab") {
        const order = billingData.labOrders.find((o) => o.id === parseInt(id));
        if (order) total += order.totalAmount || 0;
      } else if (type === "service") {
        const service = billingData.services.find((s) => s.id === parseInt(id));
        if (service) total += service.total || 0;
      } else if (type === "room") {
        const room = billingData.roomCharges?.find((r) => r.id === parseInt(id));
        if (room) total += room.charge || 0;
      }
    });

    return total;
  };

  const buildPaymentSourcesFromSelection = () => {
    const sourceIds: number[] = [];
    const sourceAmounts: Record<number, number> = {};

    selectedItems.forEach((key) => {
      const [type, id] = key.split("-");
      const numId = parseInt(id);

      if (type === "pharmacy") {
        sourceIds.push(numId);
        const sale = billingData?.pharmacy.find((s) => s.id === numId);
        if (sale) sourceAmounts[numId] = sale.netAmount || 0;
      } else if (type === "lab") {
        sourceIds.push(numId);
        const order = billingData?.labOrders.find((o) => o.id === numId);
        if (order) sourceAmounts[numId] = order.totalAmount || 0;
      }
    });

    const hasServicesOrRooms = Array.from(selectedItems).some(
      (key) => key.startsWith("service-") || key.startsWith("room-")
    );

    if (hasServicesOrRooms) {
      sourceIds.push(visitId);
    }

    return {
      sourceIds: sourceIds.length > 0 ? sourceIds : [visitId],
      sourceAmounts,
    };
  };

  const handleProcessPayment = async () => {
    if (selectedItems.size === 0) {
      showNotification("warning", "Please select items to pay", "", "topRight");
      return;
    }

    setIsProcessingPayment(true);
    try {
      const { sourceIds, sourceAmounts } = buildPaymentSourcesFromSelection();

      const payload: any = {
        sourceType: "visit",
        sourceIds,
        payments: [
          {
            amount: getSelectedItemsTotal(),
            paymentMode: "cash",
            paymentDate: new Date().toISOString().split("T")[0],
            allocationType: "manual",
          },
        ],
        sourceAmounts: sourceAmounts,
      };

      await processPayment(payload);
      showNotification("success", "Payment processed successfully", "", "topRight");
      setSelectedItems(new Set());
      fetchPaymentData();
      fetchBillingData();
    } catch (error: any) {
      console.error("Failed to process payment:", error);
      showNotification(
        "error",
        "Failed to process payment",
        error?.response?.data?.message || "An error occurred",
        "topRight"
      );
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const openPaymentModal = (context: "selected" | "outstanding") => {
    if (!hasInvoice) return;
    const outstanding = calculateOutstanding();
    if (outstanding <= 0) {
      showNotification("info", "Invoice already paid", "", "topRight");
      return;
    }
    if (context === "selected" && selectedItems.size === 0) {
      showNotification("warning", "Please select items to pay", "", "topRight");
      return;
    }
    setPaymentContext(context);
    setIsPaymentModalOpen(true);
  };

  const handleInvoicePaymentSave = async (paymentData: PaymentData) => {
    if (!hasInvoice) return;
    const selectedTotal = getSelectedItemsTotal();
    const outstanding = calculateOutstanding();
    const totalDue = paymentContext === "selected" ? selectedTotal : outstanding;
    const maxPayable = Math.min(totalDue, outstanding);
    if (maxPayable <= 0) {
      showNotification("warning", "Nothing due for payment", "", "topRight");
      return;
    }

    const paymentModes = [
      { mode: "cash", amount: paymentData.cash },
      { mode: "upi", amount: paymentData.upi },
      { mode: "card", amount: paymentData.card },
      { mode: "cheque", amount: paymentData.cheque },
      { mode: "online", amount: paymentData.online },
    ];
    const payments: Array<{
      amount: number;
      paymentMode: string;
      paymentDate: string;
      notes?: string;
      allocationType?: "manual" | "auto";
      allocationNotes?: string;
    }> = [];
    const currentDate = new Date().toISOString().split("T")[0];
    paymentModes
      .filter((p) => p.amount > 0)
      .forEach((payment) => {
        payments.push({
          amount: payment.amount,
          paymentMode: payment.mode,
          paymentDate: currentDate,
          notes: paymentData.paymentNote || undefined,
          allocationType: "manual",
        });
      });

    const advanceUsed = paymentData.advanceUsed || 0;
    if (advanceUsed > 0) {
      payments.push({
        amount: advanceUsed,
        paymentMode: "cash",
        paymentDate: currentDate,
        notes: paymentData.paymentNote || undefined,
        allocationType: "manual",
        allocationNotes: "Advance allocation",
      });
    }

    if (payments.length === 0) {
      showNotification("warning", "Please enter a payment amount", "", "topRight");
      return;
    }

    const totalPayment = payments.reduce(
      (sum, payment) => sum + (parseFloat(String(payment.amount)) || 0),
      0
    );
    if (totalPayment > maxPayable) {
      showNotification(
        "warning",
        "Payment exceeds amount due",
        `Max allowed: ${formatCurrency(maxPayable)}`,
        "topRight"
      );
      return;
    }

    setIsProcessingPayment(true);
    try {
      const { sourceIds, sourceAmounts } =
        paymentContext === "selected" && selectedItems.size > 0
          ? buildPaymentSourcesFromSelection()
          : { sourceIds: [visitId], sourceAmounts: {} };

      const payload: any = {
        sourceType: "visit",
        sourceIds,
        payments,
      };
      if (Object.keys(sourceAmounts).length > 0) {
        payload.sourceAmounts = sourceAmounts;
      }

      await processPayment(payload);
      showNotification("success", "Payment processed successfully", "", "topRight");
      setIsPaymentModalOpen(false);
      setSelectedItems(new Set());
      fetchPaymentData();
      fetchBillingData();
    } catch (error: any) {
      showNotification(
        "error",
        "Failed to process payment",
        error?.response?.data?.error?.details || "An error occurred",
        "topRight"
      );
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleUseAdvance = async () => {
    if (!hasInvoice) return;
    const advanceBalance = getAdvanceBalance();
    const outstanding = calculateOutstanding();
    const selectedTotal = getSelectedItemsTotal();
    const targetAmount = selectedItems.size > 0 ? selectedTotal : outstanding;
    const amountToApply = Math.min(advanceBalance, targetAmount);

    if (amountToApply <= 0) {
      showNotification("warning", "No advance available to allocate", "", "topRight");
      return;
    }

    setIsProcessingPayment(true);
    try {
      const { sourceIds, sourceAmounts } =
        selectedItems.size > 0 ? buildPaymentSourcesFromSelection() : {
          sourceIds: [visitId],
          sourceAmounts: {},
        };

      const payload: any = {
        sourceType: "visit",
        sourceIds,
        payments: [
          {
            amount: amountToApply,
            paymentMode: "cash",
            paymentDate: new Date().toISOString().split("T")[0],
            allocationType: "manual",
            allocationNotes: "Advance allocation",
          },
        ],
        sourceAmounts: sourceAmounts,
      };

      await processPayment(payload);
      showNotification("success", "Advance allocated successfully", "", "topRight");
      setSelectedItems(new Set());
      fetchPaymentData();
      fetchBillingData();
    } catch (error: any) {
      console.error("Failed to allocate advance:", error);
      showNotification(
        "error",
        "Failed to allocate advance",
        error?.response?.data?.message || "An error occurred",
        "topRight"
      );
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const renderSummaryView = () => {
    if (!billingData) return null;

    return (
      <div className="space-y-4">
        {/* Pharmacy Section */}
        {billingData.pharmacy.length > 0 && (
          <div className="rounded-lg border">
            <div
              className="flex justify-between items-center p-4 bg-gray-50 border-b cursor-pointer"
              onClick={() => toggleSection("pharmacy")}
            >
              <h3 className="text-lg font-semibold text-gray-900">
                Pharmacy Orders
              </h3>
              <div className="flex gap-4 items-center">
                <span className="font-semibold text-gray-700">
                  {formatCurrency(calculatePharmacyTotal())}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-600 transition-transform ${expandedSections.pharmacy ? "rotate-180" : ""
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            {expandedSections.pharmacy && (
              <div className="p-4 space-y-4">
                {billingData.pharmacy.map((sale) => (
                  <div key={sale.id} className="pb-4 border-b last:border-b-0 last:pb-0">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <span className="font-medium text-gray-700">
                          Sale #{sale.saleNumber}
                        </span>
                        <span className="ml-2 text-sm text-gray-500">
                          {formatDate(sale.saleDate)}
                        </span>
                      </div>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(sale.netAmount)}
                      </span>
                    </div>
                    <div className="mt-2 ml-4 space-y-1">
                      {sale.items.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between text-sm text-gray-600"
                        >
                          <span>
                            {item.name} × {item.quantity}
                          </span>
                          <span>{formatCurrency(item.total)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Lab Orders Section */}
        {billingData.labOrders.length > 0 && (
          <div className="rounded-lg border">
            <div
              className="flex justify-between items-center p-4 bg-gray-50 border-b cursor-pointer"
              onClick={() => toggleSection("labOrders")}
            >
              <h3 className="text-lg font-semibold text-gray-900">Lab Orders</h3>
              <div className="flex gap-4 items-center">
                <span className="font-semibold text-gray-700">
                  {formatCurrency(calculateLabTotal())}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-600 transition-transform ${expandedSections.labOrders ? "rotate-180" : ""
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            {expandedSections.labOrders && (
              <div className="p-4 space-y-4">
                {billingData.labOrders.map((order) => (
                  <div key={order.id} className="pb-4 border-b last:border-b-0 last:pb-0">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <span className="font-medium text-gray-700">
                          Order #{order.orderNumber}
                        </span>
                        <span className="ml-2 text-sm text-gray-500">
                          {formatDate(order.orderDate)}
                        </span>
                      </div>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(order.totalAmount)}
                      </span>
                    </div>
                    <div className="mt-2 ml-4 space-y-1">
                      {order.items.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between text-sm text-gray-600"
                        >
                          <span>{item.name}</span>
                          <span>{formatCurrency(item.fee)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Services Section */}
        {billingData.services.length > 0 && (
          <div className="rounded-lg border">
            <div
              className="flex justify-between items-center p-4 bg-gray-50 border-b cursor-pointer"
              onClick={() => toggleSection("services")}
            >
              <h3 className="text-lg font-semibold text-gray-900">
                Visit Services
              </h3>
              <div className="flex gap-4 items-center">
                <span className="font-semibold text-gray-700">
                  {formatCurrency(calculateServicesTotal())}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-600 transition-transform ${expandedSections.services ? "rotate-180" : ""
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            {expandedSections.services && (
              <div className="p-4">
                <div className="space-y-2">
                  {billingData.services.map((service) => (
                    <div
                      key={service.id}
                      className="flex justify-between text-sm"
                    >
                      <span className="text-gray-700">
                        {service.name} {service.quantity > 1 && `× ${service.quantity}`}
                      </span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(service.total)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Room Charges Section */}
        {billingData.roomCharges && billingData.roomCharges.length > 0 && (
          <div className="rounded-lg border">
            <div
              className="flex justify-between items-center p-4 bg-gray-50 border-b cursor-pointer"
              onClick={() => toggleSection("roomCharges")}
            >
              <h3 className="text-lg font-semibold text-gray-900">Room Charges</h3>
              <div className="flex gap-4 items-center">
                <span className="font-semibold text-gray-700">
                  {formatCurrency(calculateRoomChargesTotal())}
                </span>
                <svg
                  className={`w-5 h-5 text-gray-600 transition-transform ${expandedSections.roomCharges ? "rotate-180" : ""
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            {expandedSections.roomCharges && (
              <div className="p-4 space-y-4">
                {billingData.roomCharges.map((room, idx) => {
                  const durationDays = room.durationDays ?? 0;
                  const durationHours = room.durationHours ?? 0;
                  const isValidDuration =
                    !isNaN(durationDays) &&
                    !isNaN(durationHours) &&
                    durationDays >= 0 &&
                    durationHours >= 0;

                  return (
                    <div
                      key={`room-${room.id}-${idx}-${room.fromDate}`}
                      className="pb-4 border-b last:border-b-0 last:pb-0"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <span className="font-medium text-gray-700">
                            {room.roomName} ({room.roomType})
                          </span>
                          <div className="mt-1 text-sm text-gray-500">
                            {formatDate(room.fromDate)} - {formatDate(room.toDate)}
                          </div>
                          {isValidDuration && (
                            <div className="mt-1 text-xs text-gray-400">
                              Duration: {Math.ceil(room.durationDays)} day(s) ({room.durationHours} hours)
                            </div>
                          )}
                        </div>
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(room.charge)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Grand Total */}
        <div className="bg-gray-50 rounded-lg border-2">
          <div className="p-4">
            <div className="flex justify-between items-center">
              <span className="text-xl font-bold text-gray-900">Grand Total:</span>
              <span className="text-2xl font-bold text-primary">
                {formatCurrency(calculateGrandTotal())}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBillItemsView = () => {
    if (!billingData) return null;

    return (
      <div className="p-6 space-y-6 bg-white" style={{ minHeight: "800px" }}>
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900">Provisional Bill</h2>
          <p className="mt-2 text-gray-600">IPD Visit - {formatDate(new Date().toISOString())}</p>
        </div>

        {/* Pharmacy */}
        {billingData.pharmacy.length > 0 && (
          <div>
            <h3 className="mb-3 text-lg font-semibold">Pharmacy Orders</h3>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="p-2 text-left">Sale #</th>
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-left">Items</th>
                  <th className="p-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {billingData.pharmacy.map((sale) => (
                  <tr key={sale.id} className="border-b">
                    <td className="p-2">{sale.saleNumber}</td>
                    <td className="p-2">{formatDate(sale.saleDate)}</td>
                    <td className="p-2">
                      {sale.items.map((item, idx) => (
                        <div key={idx} className="text-sm">
                          {item.name} × {item.quantity}
                        </div>
                      ))}
                    </td>
                    <td className="p-2 text-right">{formatCurrency(sale.netAmount)}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td colSpan={3} className="p-2 text-right">Subtotal:</td>
                  <td className="p-2 text-right">{formatCurrency(calculatePharmacyTotal())}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Lab Orders */}
        {billingData.labOrders.length > 0 && (
          <div>
            <h3 className="mb-3 text-lg font-semibold">Lab Orders</h3>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="p-2 text-left">Order #</th>
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-left">Test</th>
                  <th className="p-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {billingData.labOrders.map((order) => (
                  <tr key={order.id} className="border-b">
                    <td className="p-2">{order.orderNumber}</td>
                    <td className="p-2">{formatDate(order.orderDate)}</td>
                    <td className="p-2">
                      {order.items.map((item, idx) => (
                        <div key={idx}>{item.name}</div>
                      ))}
                    </td>
                    <td className="p-2 text-right">{formatCurrency(order.totalAmount)}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td colSpan={3} className="p-2 text-right">Subtotal:</td>
                  <td className="p-2 text-right">{formatCurrency(calculateLabTotal())}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Services */}
        {billingData.services.length > 0 && (
          <div>
            <h3 className="mb-3 text-lg font-semibold">Visit Services</h3>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="p-2 text-left">Service</th>
                  <th className="p-2 text-left">Quantity</th>
                  <th className="p-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {billingData.services.map((service) => (
                  <tr key={service.id} className="border-b">
                    <td className="p-2">{service.name}</td>
                    <td className="p-2">{service.quantity}</td>
                    <td className="p-2 text-right">{formatCurrency(service.total)}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td colSpan={2} className="p-2 text-right">Subtotal:</td>
                  <td className="p-2 text-right">{formatCurrency(calculateServicesTotal())}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Room Charges */}
        {billingData.roomCharges && billingData.roomCharges.length > 0 && (
          <div>
            <h3 className="mb-3 text-lg font-semibold">Room Charges</h3>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="p-2 text-left">Room</th>
                  <th className="p-2 text-left">Period</th>
                  <th className="p-2 text-left">Duration</th>
                  <th className="p-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {billingData.roomCharges.map((room, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="p-2">
                      {room.roomName} ({room.roomType})
                    </td>
                    <td className="p-2 text-sm">
                      {formatDate(room.fromDate)} - {formatDate(room.toDate)}
                    </td>
                    <td className="p-2 text-sm">
                      {Math.ceil(room.durationDays)} day(s)
                    </td>
                    <td className="p-2 text-right">{formatCurrency(room.charge)}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td colSpan={3} className="p-2 text-right">Subtotal:</td>
                  <td className="p-2 text-right">{formatCurrency(calculateRoomChargesTotal())}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Grand Total */}
        <div className="pt-4 mt-8 border-t-2">
          <div className="flex justify-between items-center">
            <span className="text-2xl font-bold text-gray-900">Grand Total:</span>
            <span className="text-3xl font-bold text-primary">
              {formatCurrency(calculateGrandTotal())}
            </span>
          </div>
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={() => window.print()}
            className="px-6 py-2 text-white rounded-lg bg-primary hover:bg-primary-dark"
          >
            Print Bill
          </button>
        </div>
      </div>
    );
  };

  const renderPreInvoiceView = () => {
    if (!billingData) return null;
    const editableTotal = calculateEditableTotal();
    const hasItems =
      billingData.pharmacy.length > 0 ||
      billingData.labOrders.length > 0 ||
      billingData.services.length > 0 ||
      (billingData.roomCharges && billingData.roomCharges.length > 0);

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg border">
          <div>
            <div className="text-sm text-gray-600">Unallocated Advance</div>
            <div className="mt-1 text-xl font-bold text-green-700">
              {formatCurrency(getAdvanceBalance())}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4 p-4 bg-yellow-50 rounded-lg border sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm text-gray-600">No bill generated for this visit</div>
            <div className="text-lg font-semibold text-gray-900">
              Collect advance or generate bill
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setIsAdvanceModalOpen(true)}
              className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700"
            >
              Collect Advance
            </button>
            <button
              onClick={handleGenerateInvoice}
              disabled={isGeneratingInvoice || !hasItems}
              className="px-4 py-2 text-white rounded-lg bg-primary hover:bg-primary-dark disabled:opacity-50"
            >
              {isGeneratingInvoice ? "Generating..." : "Generate Bill"}
            </button>
          </div>
        </div>

        <div className="border rounded-lg flex flex-col max-h-[60vh]">
          <div className="p-4 bg-gray-50 border-b shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">Edit Rates</h3>
          </div>
          <div className="overflow-y-auto flex-1 p-4 space-y-3 min-h-0">
            {billingData.pharmacy.map((sale) => {
              const key = `pharmacy-${sale.id}`;
              return (
                <div key={key} className="flex gap-4 justify-between items-center p-3 rounded-lg border">
                  <div>
                    <div className="font-medium">Pharmacy Sale #{sale.saleNumber}</div>
                    <div className="text-sm text-gray-500">{formatDate(sale.saleDate)}</div>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={getRateValue(key, sale.netAmount || 0)}
                    onChange={(e) =>
                      setRateOverrides((prev) => ({
                        ...prev,
                        [key]: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className="px-3 py-2 w-32 text-right rounded-lg border"
                  />
                </div>
              );
            })}

            {billingData.labOrders.map((order) => {
              const key = `lab-${order.id}`;
              return (
                <div key={key} className="flex gap-4 justify-between items-center p-3 rounded-lg border">
                  <div>
                    <div className="font-medium">Lab Order #{order.orderNumber}</div>
                    <div className="text-sm text-gray-500">{formatDate(order.orderDate)}</div>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={getRateValue(key, order.totalAmount || 0)}
                    onChange={(e) =>
                      setRateOverrides((prev) => ({
                        ...prev,
                        [key]: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className="px-3 py-2 w-32 text-right rounded-lg border"
                  />
                </div>
              );
            })}

            {billingData.services.map((service) => {
              const key = `service-${service.id}`;
              return (
                <div key={key} className="flex gap-4 justify-between items-center p-3 rounded-lg border">
                  <div>
                    <div className="font-medium">{service.name}</div>
                    <div className="text-sm text-gray-500">
                      Quantity: {service.quantity}
                    </div>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={getRateValue(key, service.total || 0)}
                    onChange={(e) =>
                      setRateOverrides((prev) => ({
                        ...prev,
                        [key]: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className="px-3 py-2 w-32 text-right rounded-lg border"
                  />
                </div>
              );
            })}

            {billingData.roomCharges?.map((room, idx) => {
              const key = `room-${room.id}-${idx}`;
              return (
                <div key={key} className="flex gap-4 justify-between items-center p-3 rounded-lg border">
                  <div>
                    <div className="font-medium">
                      {room.roomName} ({room.roomType})
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatDate(room.fromDate)} - {formatDate(room.toDate)}
                    </div>
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={getRateValue(key, room.charge || 0)}
                    onChange={(e) =>
                      setRateOverrides((prev) => ({
                        ...prev,
                        [key]: parseFloat(e.target.value) || 0,
                      }))
                    }
                    className="px-3 py-2 w-32 text-right rounded-lg border"
                  />
                </div>
              );
            })}

            <div className="p-3 space-y-3 rounded-lg border">
              <div className="flex justify-between items-center">
                <div className="font-medium text-gray-900">Custom Items</div>
                <button
                  onClick={addCustomItem}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  Add Item
                </button>
              </div>

              {customItems.length === 0 && (
                <div className="text-sm text-gray-500">No custom items added.</div>
              )}

              {customItems.map((item) => (
                <div key={item.id} className="grid grid-cols-12 gap-3 items-center">
                  <input
                    type="text"
                    placeholder="Item name"
                    value={item.name}
                    onChange={(e) => updateCustomItem(item.id, { name: e.target.value })}
                    className="col-span-6 px-3 py-2 rounded-lg border"
                  />
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={item.quantity}
                    onChange={(e) =>
                      updateCustomItem(item.id, { quantity: parseInt(e.target.value, 10) || 0 })
                    }
                    className="col-span-2 px-3 py-2 text-right rounded-lg border"
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.rate}
                    onChange={(e) =>
                      updateCustomItem(item.id, { rate: parseFloat(e.target.value) || 0 })
                    }
                    className="col-span-3 px-3 py-2 text-right rounded-lg border"
                  />
                  <button
                    onClick={() => removeCustomItem(item.id)}
                    className="col-span-1 text-sm text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            {!hasItems && (
              <div className="text-sm text-gray-500">No bill items available.</div>
            )}
          </div>
          <div className="flex justify-between items-center p-4 bg-gray-50 border-t shrink-0">
            <span className="font-semibold text-gray-900">Bill Total</span>
            <span className="text-xl font-bold text-primary">
              {formatCurrency(editableTotal)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderPaymentsView = () => {
    if (!hasInvoice) {
      return renderPreInvoiceView();
    }
    const outstanding = calculateOutstanding();
    const unallocatedBalance = getAdvanceBalance();
    const selectedTotal = getSelectedItemsTotal();
    const invoiceStatus = invoiceData?.invoice?.status;
    const isInvoicePaid = invoiceStatus === "paid" || outstanding <= 0;

    return (
      <div className="space-y-6">
        {/* Balance Summary */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg border">
            <div className="text-sm text-gray-600">Current Balance</div>
            <div className="mt-1 text-2xl font-bold text-gray-900">
              {formatCurrency(calculateGrandTotal())}
            </div>
          </div>
          <div className="p-4 bg-yellow-50 rounded-lg border">
            <div className="text-sm text-gray-600">Outstanding</div>
            <div className="mt-1 text-2xl font-bold text-yellow-700">
              {isInvoicePaid ? "Paid" : formatCurrency(outstanding)}
            </div>
          </div>
        </div>

        {isInvoicePaid && (
          <div className="p-4 bg-green-50 rounded-lg border">
            <div className="text-sm text-gray-600">Invoice Status</div>
            <div className="mt-1 text-lg font-semibold text-green-700">
              Paid in full
            </div>
          </div>
        )}

        {/* Unallocated Balance */}
        <div className="p-4 bg-green-50 rounded-lg border">
          <div className="flex justify-between items-center">
            <div>
              <div className="text-sm text-gray-600">Unallocated Balance</div>
              <div className="mt-1 text-xl font-bold text-green-700">
                {formatCurrency(unallocatedBalance)}
              </div>
            </div>

          </div>
        </div>

        {!isInvoicePaid && (
          <div className="border rounded-lg flex flex-col max-h-[60vh]">
            <div className="p-4 bg-gray-50 border-b shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">Select Items to Pay</h3>
            </div>
            <div className="overflow-y-auto flex-1 p-4 space-y-4 min-h-0">
              {billingData?.pharmacy.map((sale) => (
                <label
                  key={`pharmacy-${sale.id}`}
                  className="flex justify-between items-center p-3 rounded-lg border cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex gap-3 items-center">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(`pharmacy-${sale.id}`)}
                      onChange={() => handleItemSelection(`pharmacy-${sale.id}`)}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="font-medium">Pharmacy Sale #{sale.saleNumber}</div>
                      <div className="text-sm text-gray-500">{formatDate(sale.saleDate)}</div>
                    </div>
                  </div>
                  <span className="font-semibold">{formatCurrency(sale.netAmount)}</span>
                </label>
              ))}

              {billingData?.labOrders.map((order) => (
                <label
                  key={`lab-${order.id}`}
                  className="flex justify-between items-center p-3 rounded-lg border cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex gap-3 items-center">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(`lab-${order.id}`)}
                      onChange={() => handleItemSelection(`lab-${order.id}`)}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="font-medium">Lab Order #{order.orderNumber}</div>
                      <div className="text-sm text-gray-500">{formatDate(order.orderDate)}</div>
                    </div>
                  </div>
                  <span className="font-semibold">{formatCurrency(order.totalAmount)}</span>
                </label>
              ))}

              {billingData?.services.map((service) => (
                <label
                  key={`service-${service.id}`}
                  className="flex justify-between items-center p-3 rounded-lg border cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex gap-3 items-center">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(`service-${service.id}`)}
                      onChange={() => handleItemSelection(`service-${service.id}`)}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="font-medium">{service.name}</div>
                      <div className="text-sm text-gray-500">
                        Quantity: {service.quantity}
                      </div>
                    </div>
                  </div>
                  <span className="font-semibold">{formatCurrency(service.total)}</span>
                </label>
              ))}

              {billingData?.roomCharges?.map((room, idx) => (
                <label
                  key={`room-${room.id}-${idx}`}
                  className="flex justify-between items-center p-3 rounded-lg border cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex gap-3 items-center">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(`room-${room.id}-${idx}`)}
                      onChange={() => handleItemSelection(`room-${room.id}-${idx}`)}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="font-medium">
                        {room.roomName} ({room.roomType})
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(room.fromDate)} - {formatDate(room.toDate)}
                      </div>
                    </div>
                  </div>
                  <span className="font-semibold">{formatCurrency(room.charge)}</span>
                </label>
              ))}
            </div>

            {selectedItems.size > 0 && (
              <div className="p-4 bg-gray-50 border-t shrink-0">
                <div className="flex justify-between items-center mb-4">
                  <span className="font-semibold text-gray-900">Selected Total:</span>
                  <span className="text-xl font-bold text-primary">
                    {formatCurrency(selectedTotal)}
                  </span>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => openPaymentModal("selected")}
                    disabled={isProcessingPayment}
                    className="flex-1 px-4 py-2 text-white rounded-lg bg-primary hover:bg-primary-dark disabled:opacity-50"
                  >
                    {isProcessingPayment ? "Processing..." : "Make Payment"}
                  </button>
                  <button
                    onClick={() => {
                      // TODO: Implement new payment modal
                      showNotification("info", "New Payment", "Feature coming soon", "topRight");
                    }}
                    className="flex-1 px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700"
                  >
                    New Payment
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Payment Allocations */}
        {invoiceData?.invoice?.paymentAllocations && (
          <div className="rounded-lg border">
            <div className="p-4 bg-gray-50 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Payment Allocations</h3>
            </div>
            <div className="p-4">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-left">Receipt #</th>
                    <th className="p-2 text-left">Amount</th>
                    <th className="p-2 text-left">Allocated</th>
                    <th className="p-2 text-left">Payment Mode</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceData.invoice.paymentAllocations.map((alloc: any, idx: number) => (
                    <tr key={idx} className="border-b">
                      <td className="p-2">
                        {formatDate(alloc.patientTransaction?.paymentDate)}
                      </td>
                      <td className="p-2">
                        {alloc.patientTransaction?.receiptNumber || "-"}
                      </td>
                      <td className="p-2">
                        {formatCurrency(alloc.patientTransaction?.amount)}
                      </td>
                      <td className="p-2">{formatCurrency(alloc.allocatedAmount)}</td>
                      <td className="p-2">{alloc.patientTransaction?.paymentMode || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Make Payment (Outstanding) */}
        {!isInvoicePaid && (
          <div className="flex justify-end">
            <button
              onClick={() => openPaymentModal("outstanding")}
              disabled={isProcessingPayment}
              className="px-4 py-2 text-white rounded-lg bg-primary hover:bg-primary-dark disabled:opacity-50"
            >
              Make Payment
            </button>
          </div>
        )}
      </div>
    );
  };

  const handleSaveInsurance = async () => {
    const parsedClaimedAmount = insuranceForm.claimedAmount ? parseFloat(insuranceForm.claimedAmount) : NaN;
    if (!(Number.isFinite(parsedClaimedAmount) && parsedClaimedAmount > 0)) {
      showNotification("error", "Validation error", "Claimed amount must be greater than 0.", "topRight");
      return;
    }

    const today = format(new Date(), "yyyy-MM-dd");
    const effectiveStatus = (insuranceForm.status || "pending").trim() || "pending";
    const effectiveClaimDate = (insuranceForm.claimDate || today).trim() || today;
    const normalizedStatus = String(effectiveStatus ?? "").toLowerCase();
    const requiresApprovedAmount = normalizedStatus === "approved" || normalizedStatus === "partial" || normalizedStatus === "partially_approved";
    const parsedApprovedAmount = insuranceForm.approvedAmount ? parseFloat(insuranceForm.approvedAmount) : NaN;
    if (requiresApprovedAmount && !(Number.isFinite(parsedApprovedAmount) && parsedApprovedAmount > 0)) {
      showNotification("error", "Validation error", "Approved amount must be greater than 0 for Approved / Partially Approved claims.", "topRight");
      return;
    }
    if (requiresApprovedAmount && Number.isFinite(parsedApprovedAmount) && parsedApprovedAmount > parsedClaimedAmount) {
      showNotification("error", "Validation error", "Approved amount cannot be greater than claimed amount.", "topRight");
      return;
    }
    const effectiveApprovalDate = (insuranceForm.approvalDate || "").trim();
    if (requiresApprovedAmount && !effectiveApprovalDate) {
      showNotification("error", "Validation error", "Approval date is required for Approved / Partially Approved claims.", "topRight");
      return;
    }

    setIsSavingInsurance(true);
    const payload = {
      insuranceProviderId: insuranceForm.insuranceProviderId ?? undefined,
      policyNumber: insuranceForm.policyNumber.trim() || undefined,
      coverageType: insuranceForm.coverageType,
      claimedAmount: parsedClaimedAmount,
      approvedAmount: insuranceForm.approvedAmount ? parseFloat(insuranceForm.approvedAmount) : undefined,
      rejectedAmount: insuranceForm.rejectedAmount ? parseFloat(insuranceForm.rejectedAmount) : undefined,
      status: effectiveStatus,
      claimDate: effectiveClaimDate,
      approvalDate: effectiveApprovalDate || undefined,
      remarks: insuranceForm.remarks.trim() || undefined,
    };
    try {
      if (editingClaimId != null) {
        await updateVisitInsuranceClaim(visitId, editingClaimId, payload);
        showNotification("success", "Insurance claim updated", "", "topRight");
      } else {
        await saveVisitInsuranceClaim(visitId, payload);
        showNotification("success", "Insurance claim added", "", "topRight");
      }
      setEditingClaimId(null);
      setInsuranceViewMode("list");
      fetchInsuranceData();
    } catch (error: any) {
      showNotification("error", "Error", error?.response?.data?.message || "Failed to save insurance claim", "topRight");
    } finally {
      setIsSavingInsurance(false);
    }
  };

  const formatClaimStatus = (status: string) => {
    if (!status) return "—";
    return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const openAddInsuranceClaimForm = () => {
    setEditingClaimId(null);
    const today = format(new Date(), "yyyy-MM-dd");
    const baseDefaultForm: InsuranceClaimForm = {
      insuranceProviderId: null,
      policyNumber: "",
      coverageType: "reimbursement" as const,
      claimedAmount: "",
      approvedAmount: "",
      rejectedAmount: "",
      status: "pending",
      claimDate: today,
      approvalDate: "",
      remarks: "",
    };
    const defaultForm: InsuranceClaimForm = {
      ...baseDefaultForm,
      ...(defaultInsuranceFormFromAdmission ?? {}),
      status: (defaultInsuranceFormFromAdmission?.status || baseDefaultForm.status) as string,
      claimDate: (defaultInsuranceFormFromAdmission?.claimDate || baseDefaultForm.claimDate) as string,
    };
    setInsuranceForm(defaultForm);
    setInsuranceViewMode("form");
  };

  const openEditInsuranceClaimForm = (claim: any) => {
    const id = claim?.id ?? claim?.claim_id;
    if (id != null) setEditingClaimId(Number(id));
    const providerId = claim?.insuranceProviderId ?? claim?.insurance_provider_id ?? null;
    setInsuranceForm({
      insuranceProviderId: providerId ?? null,
      policyNumber: claim?.policyNumber ?? claim?.policy_number ?? "",
      coverageType: (claim?.coverageType ?? claim?.coverage_type ?? "reimbursement") as "reimbursement" | "cashless",
      claimedAmount: claim != null && (claim.claimedAmount != null || claim.claimed_amount != null) ? String(claim.claimedAmount ?? claim.claimed_amount ?? "") : "",
      approvedAmount: claim != null && (claim.approvedAmount != null || claim.approved_amount != null) ? String(claim.approvedAmount ?? claim.approved_amount ?? "") : "",
      rejectedAmount: claim != null && (claim.rejectedAmount != null || claim.rejected_amount != null) ? String(claim.rejectedAmount ?? claim.rejected_amount ?? "") : "",
      status: claim?.status ?? "",
      claimDate: claim?.claimDate ?? claim?.claim_date ? format(new Date(claim.claim_date ?? claim.claimDate), "yyyy-MM-dd") : "",
      approvalDate: claim?.approvalDate ?? claim?.approval_date ? format(new Date(claim.approval_date ?? claim.approvalDate), "yyyy-MM-dd") : "",
      remarks: claim?.remarks ?? "",
    });
    setInsuranceViewMode("form");
  };

  const renderInsuranceListView = () => {
    const claims = insuranceClaimsList ?? [];
    const blockingStatuses = new Set(["pending", "submitted", "partial"]);
    const hasBlockingClaim = claims.some((c: any) =>
      blockingStatuses.has(String(c?.status ?? "").toLowerCase())
    );
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Insurance Claims</h2>
          <button
            type="button"
            onClick={openAddInsuranceClaimForm}
            disabled={hasBlockingClaim}
            title={hasBlockingClaim ? "You cannot add a new claim while any claim is Pending / Submitted / Partial." : undefined}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed bg-primary hover:bg-primary-dark"
          >
            Add insurance claim
          </button>
        </div>
        {hasBlockingClaim && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            You cannot add a new claim while any claim is Pending / Submitted / Partial. Edit or resolve those claim(s) first.
          </p>
        )}
        {claims.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center text-gray-500">
            <p className="mb-4">No insurance claim recorded for this visit yet.</p>
            <button
              type="button"
              onClick={openAddInsuranceClaimForm}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark"
            >
              Add insurance claim
            </button>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Edit</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Insurance Provider</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Policy Number</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coverage Type</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Claimed (₹)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Approved (₹)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Rejected (₹)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Claim Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approval Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {claims.map((claim: any) => (
                  <tr key={claim.id ?? claim.claim_id ?? Math.random()}>
                    <td className="px-4 py-3 text-sm text-left whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => openEditInsuranceClaimForm(claim)}
                        className="px-3 py-1.5 text-sm font-medium text-primary border border-primary rounded-md hover:bg-primary/10 hover:border-primary-dark"
                      >
                        Edit
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{formatClaimStatus(claim.status ?? "")}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {claim.insuranceProvider?.name ?? claim.insurance_provider?.name ?? claim.insuranceProviderName ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{claim.policyNumber ?? claim.policy_number ?? "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 capitalize">{claim.coverageType ?? claim.coverage_type ?? "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(claim.claimedAmount ?? claim.claimed_amount)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(claim.approvedAmount ?? claim.approved_amount)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(claim.rejectedAmount ?? claim.rejected_amount)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                      {claim.claimDate ?? claim.claim_date ? format(new Date(claim.claim_date ?? claim.claimDate), "dd MMM yyyy") : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                      {claim.approvalDate ?? claim.approval_date ? format(new Date(claim.approval_date ?? claim.approvalDate), "dd MMM yyyy") : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => openEditInsuranceClaimForm(claim)}
                        className="px-3 py-1.5 text-sm font-medium text-primary border border-primary rounded-md hover:bg-primary/10 hover:border-primary-dark"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderInsuranceFormView = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Insurance & Claim</h2>
        <button
          type="button"
          onClick={() => { setEditingClaimId(null); setInsuranceViewMode("list"); }}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Back to list
        </button>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Insurance Provider</label>
            <select
              value={insuranceForm.insuranceProviderId ?? ""}
              onChange={(e) => setInsuranceForm({ ...insuranceForm, insuranceProviderId: e.target.value ? Number(e.target.value) : null })}
              className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-primary focus:border-primary"
            >
              <option value="">Select provider</option>
              {insuranceProviders.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Policy Number</label>
            <input
              type="text"
              value={insuranceForm.policyNumber}
              onChange={(e) => setInsuranceForm({ ...insuranceForm, policyNumber: e.target.value })}
              className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-primary focus:border-primary"
              placeholder="Policy number"
            />
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Coverage Type</label>
            <div className="flex gap-6 mt-2">
              <label className="inline-flex gap-2 items-center cursor-pointer">
                <input
                  type="radio"
                  name="coverageType"
                  checked={insuranceForm.coverageType === "reimbursement"}
                  onChange={() => setInsuranceForm({ ...insuranceForm, coverageType: "reimbursement" })}
                  className="border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700">Reimbursement</span>
              </label>
              <label className="inline-flex gap-2 items-center cursor-pointer">
                <input
                  type="radio"
                  name="coverageType"
                  checked={insuranceForm.coverageType === "cashless"}
                  onChange={() => setInsuranceForm({ ...insuranceForm, coverageType: "cashless" })}
                  className="border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700">Cashless</span>
              </label>
            </div>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Claimed Amount (₹)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={insuranceForm.claimedAmount}
              onChange={(e) => setInsuranceForm({ ...insuranceForm, claimedAmount: e.target.value })}
              className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-primary focus:border-primary"
              placeholder="0.00"
            />
            {!(Number.isFinite(Number(insuranceForm.claimedAmount)) && Number(insuranceForm.claimedAmount) > 0) && (
              <p className="mt-1 text-xs text-red-600">Claimed amount must be greater than 0.</p>
            )}
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Approved Amount (₹)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={insuranceForm.approvedAmount}
              onChange={(e) => setInsuranceForm({ ...insuranceForm, approvedAmount: e.target.value })}
              className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-primary focus:border-primary"
              placeholder="0.00"
            />
            {(() => {
              const normalizedStatus = String(insuranceForm.status ?? "").toLowerCase();
              const requiresApprovedAmount = normalizedStatus === "approved" || normalizedStatus === "partial" || normalizedStatus === "partially_approved";
              if (!requiresApprovedAmount) return null;
              const approved = Number(insuranceForm.approvedAmount);
              const claimed = Number(insuranceForm.claimedAmount);
              if (!(Number.isFinite(approved) && approved > 0)) {
                return <p className="mt-1 text-xs text-red-600">Approved amount must be greater than 0 for Approved / Partially Approved claims.</p>;
              }
              if (Number.isFinite(claimed) && approved > claimed) {
                return <p className="mt-1 text-xs text-red-600">Approved amount cannot be greater than claimed amount.</p>;
              }
              return null;
            })()}
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Rejected Amount (₹)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={insuranceForm.rejectedAmount}
              onChange={(e) => setInsuranceForm({ ...insuranceForm, rejectedAmount: e.target.value })}
              className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-primary focus:border-primary"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Status</label>
            <select
              value={insuranceForm.status}
              onChange={(e) => setInsuranceForm({ ...insuranceForm, status: e.target.value })}
              className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-primary focus:border-primary"
            >
              <option value="">Select status</option>
              <option value="pending">Pending</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="partially_approved">Partially Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Claim Date</label>
            <input
              type="date"
              value={insuranceForm.claimDate}
              onChange={(e) => setInsuranceForm({ ...insuranceForm, claimDate: e.target.value })}
              className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-primary focus:border-primary"
            />
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Approval Date</label>
            <input
              type="date"
              value={insuranceForm.approvalDate}
              onChange={(e) => setInsuranceForm({ ...insuranceForm, approvalDate: e.target.value })}
              className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-primary focus:border-primary"
            />
            {(() => {
              const normalizedStatus = String(insuranceForm.status ?? "").toLowerCase();
              const requiresApprovedAmount = normalizedStatus === "approved" || normalizedStatus === "partial" || normalizedStatus === "partially_approved";
              if (!requiresApprovedAmount) return null;
              return !String(insuranceForm.approvalDate ?? "").trim() ? (
                <p className="mt-1 text-xs text-red-600">Approval date is required for Approved / Partially Approved claims.</p>
              ) : null;
            })()}
          </div>
        </div>
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">Remarks</label>
          <textarea
            value={insuranceForm.remarks}
            onChange={(e) => setInsuranceForm({ ...insuranceForm, remarks: e.target.value })}
            rows={3}
            className="px-3 py-2 w-full rounded-md border border-gray-300 focus:ring-primary focus:border-primary"
            placeholder="Remarks"
          />
        </div>
        <div className="flex justify-end pt-4">
          <button
            type="button"
            onClick={handleSaveInsurance}
            disabled={
              isSavingInsurance ||
              !(Number.isFinite(Number(insuranceForm.claimedAmount)) && Number(insuranceForm.claimedAmount) > 0) ||
              ((() => {
                const normalizedStatus = String(insuranceForm.status ?? "").toLowerCase();
                const requiresApprovedAmount = normalizedStatus === "approved" || normalizedStatus === "partial" || normalizedStatus === "partially_approved";
                if (!requiresApprovedAmount) return false;
                const approved = Number(insuranceForm.approvedAmount);
                const claimed = Number(insuranceForm.claimedAmount);
                const approvedAmountInvalid = !(Number.isFinite(approved) && approved > 0) || (Number.isFinite(claimed) && approved > claimed);
                const approvalDateMissing = !String(insuranceForm.approvalDate ?? "").trim();
                return approvedAmountInvalid || approvalDateMissing;
              })())
            }
            className="px-4 py-2 text-white rounded-lg bg-primary hover:bg-primary-dark disabled:opacity-50"
          >
            {isSavingInsurance ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
  );

  const renderInsuranceView = () => {
    if (isLoadingInsurance) return <LoaderComponent />;
    return insuranceViewMode === "list" ? renderInsuranceListView() : renderInsuranceFormView();
  };

  if (!isOpen) return null;

  return (
    <div className="overflow-hidden fixed inset-0 z-50">
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        aria-hidden="true"
        onClick={onClose}
      ></div>

      <div className="flex justify-center items-center p-4 min-h-screen">
        <div className="inline-block w-full max-w-7xl h-[90vh] overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex justify-between p-4 text-white bg-primary">
            <span className="text-lg font-bold">IPD Visit Billing</span>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 focus:outline-none"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Main Content */}
          <div className="flex h-[calc(90vh-64px)]">
            {/* Left Navigation - 25% */}
            <div className="overflow-y-auto w-1/4 bg-gray-50 border-r">
              <nav className="p-4 space-y-2">
                <button
                  onClick={() => setActiveNav("summary")}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${activeNav === "summary"
                      ? "bg-primary text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  Summary
                </button>
                <button
                  onClick={() => setActiveNav("billItems")}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${activeNav === "billItems"
                      ? "bg-primary text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  Bill Items
                </button>
                <button
                  onClick={() => {
                    setActiveNav("payments");
                    fetchPaymentData();
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${activeNav === "payments"
                      ? "bg-primary text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  Payments & Allocations
                </button>
                <button
                  onClick={() => {
                    setActiveNav("insurance");
                    setInsuranceViewMode("list");
                    fetchInsuranceData();
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${activeNav === "insurance"
                      ? "bg-primary text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  Insurance
                </button>
              </nav>
            </div>

            {/* Right Content - 75% */}
            <div className="overflow-y-auto p-6 w-3/4">
              {activeNav === "insurance" ? (
                isLoadingInsurance ? <LoaderComponent /> : renderInsuranceView()
              ) : isLoading ? (
                <LoaderComponent />
              ) : (
                <>
                  {activeNav === "summary" && renderSummaryView()}
                  {activeNav === "billItems" && renderBillItemsView()}
                  {activeNav === "payments" && renderPaymentsView()}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {isAdvanceModalOpen && (
        <PaymentModal
          isOpen={isAdvanceModalOpen}
          onClose={() => setIsAdvanceModalOpen(false)}
          total={0}
          discount={0}
          patientId={patientId}
          sourceType="visit"
          onSave={handleAdvanceSave}
          onPaymentSuccess={() => setIsAdvanceModalOpen(false)}
        />
      )}

      {isPaymentModalOpen && (
        <PaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          total={
            paymentContext === "selected"
              ? getSelectedItemsTotal()
              : calculateOutstanding()
          }
          discount={0}
          patientId={patientId}
          sourceType="visit"
          advanceBalance={getAdvanceBalance()}
          onSave={handleInvoicePaymentSave}
          onPaymentSuccess={() => setIsPaymentModalOpen(false)}
        />
      )}
    </div>
  );
};

export default IPDBillingModal;
